// src/pages/SessionSetup.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { doc, setDoc, getDoc, collection, getDocs, query, where, orderBy, limit, deleteField, onSnapshot, updateDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import Header from '../components/Header';
import ConfirmationDialog from '../components/ConfirmationDialog';
import MobileScrollInput from '../components/MobileScrollInput';
import CompensationDisplay from '../components/CompensationDisplay';
import GearingCalculator from '../components/GearingCalculator';
import EnginePowerGraph from '../components/EnginePowerGraph';
import TractiveEffortGraph from '../components/TractiveEffortGraph';
import DamperForceGraph from '../components/DamperForceGraph';
import PassphrasePrompt from '../components/PassphrasePrompt';
import { IMAGE_PATHS } from '../config';
import { updateLastModified } from '../utils/backup';
import { isEncryptionEnabled, getStoredPassphrase, encryptData, decryptData, storePassphrase } from '../utils/encryption';
import { decryptSessions } from '../utils/sessionLoader';
import { convertToNative, convertFromNative } from '../components/VehicleEditor/Units';
import { MandatoryParameters, CarTemplates } from '../components/VehicleEditor/DefaultParameters';
import TireTemperatureInput from '../components/VehicleEditor/TireTemperatureInput';
import '../styles.css';

const convertToGroups = (params) => {
  if (Array.isArray(params)) {
    return [{ title: 'Default', params }];
  }
  return Object.entries(params).map(([title, params]) => ({ title, params }));
};

function SessionSetup() {
  const [sessionName, setSessionName] = useState('');
  const [comment, setComment] = useState('');
  const [time, setTime] = useState('');
  const [tabs, setTabs] = useState([]);
  const [params, setParams] = useState({});
  const [initialParams, setInitialParams] = useState({});
  const [notes, setNotes] = useState({});
  const [fieldMeta, setFieldMeta] = useState({});
  const previousNotesRef = useRef({});
  const [comparison, setComparison] = useState(null);
  const [syncStatus, setSyncStatus] = useState('syncing');
  const [showDuplicatePrompt, setShowDuplicatePrompt] = useState(false);
  const [duplicateMessage, setDuplicateMessage] = useState('Duplicate Last Session?');
  const [currentTab, setCurrentTab] = useState(0);
  const [vehicleId, setVehicleId] = useState(null);
  const [eventId, setEventId] = useState(null);
  const [sessionGroupId, setSessionGroupId] = useState(null);
  const [sessionVehicleIds, setSessionVehicleIds] = useState(null);
  const [vehicleName, setVehicleName] = useState('');
  // Vehicle default params, captured on load so an eagerly-created (empty) session
  // can be seeded from the vehicle config the first time its setup sheet is opened.
  const defaultParamsRef = useRef({});
  const needsDefaultParamsRef = useRef(false);
  const [initialData, setInitialData] = useState(null);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [showSelectOther, setShowSelectOther] = useState(false);
  const [showCompareModal, setShowCompareModal] = useState(false);
  const [events, setEvents] = useState([]);
  const [sessionsByEvent, setSessionsByEvent] = useState({});
  const [previousSession, setPreviousSession] = useState(null);
  const [powerData, setPowerData] = useState([]);
  const [damperSetup, setDamperSetup] = useState({});
  const [comparisonEventName, setComparisonEventName] = useState('');
  const [tires, setTires] = useState([]);
  const [loadedNativeParams, setLoadedNativeParams] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [helpParam, setHelpParam] = useState(null);
  const [showCheatSheet, setShowCheatSheet] = useState(false);
  const [updatedParams, setUpdatedParams] = useState(new Set());
  const previousSetupRef = useRef(null);
  const [paramHistory, setParamHistory] = useState([]);
  const [redoHistory, setRedoHistory] = useState([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const saveToHistory = () => {
    const currentState = {
      params: { ...params },
      sessionName,
      comment,
      time,
      notes: { ...notes },
      powerData: [...powerData],
      damperSetup: { ...damperSetup }
    };
    setParamHistory(prev => [...prev.slice(-9), currentState]); // Keep last 10 states
    setRedoHistory([]); // Clear redo history when new change is made
  };

  const handleParamChange = (key, value) => {
    saveToHistory();
    setParams(prev => ({ ...prev, [key]: value }));
    setUpdatedParams(prev => {
      const newSet = new Set(prev);
      newSet.delete(key);
      return newSet;
    });
  };

  const handleUndo = () => {
    if (paramHistory.length > 0) {
      // Save current state to redo history
      const currentState = {
        params: { ...params },
        sessionName,
        comment,
        time,
        notes: { ...notes },
        powerData: [...powerData],
        damperSetup: { ...damperSetup }
      };
      setRedoHistory(prev => [...prev.slice(-19), currentState]); // Keep last 20 redo states

      const lastState = paramHistory[paramHistory.length - 1];
      setParams(lastState.params);
      setSessionName(lastState.sessionName);
      setComment(lastState.comment);
      setTime(lastState.time);
      setNotes(lastState.notes);
      setPowerData(lastState.powerData);
      setDamperSetup(lastState.damperSetup || {});
      setParamHistory(prev => prev.slice(0, -1));
      // Do not clear highlights on undo
    }
  };

  const handleRedo = () => {
    if (redoHistory.length > 0) {
      // Save current state to undo history
      const currentState = {
        params: { ...params },
        sessionName,
        comment,
        time,
        notes: { ...notes },
        powerData: [...powerData],
        damperSetup: { ...damperSetup }
      };
      setParamHistory(prev => [...prev.slice(-19), currentState]);

      const nextState = redoHistory[redoHistory.length - 1];
      setParams(nextState.params);
      setSessionName(nextState.sessionName);
      setComment(nextState.comment);
      setTime(nextState.time);
      setNotes(nextState.notes);
      setPowerData(nextState.powerData);
      setDamperSetup(nextState.damperSetup || {});
      setRedoHistory(prev => prev.slice(0, -1));
      // Do not clear highlights on redo
    }
  };

  const hasTabUpdated = (tab) => {
    for (const group of tab.groups) {
      for (const param of group.params) {
        if (updatedParams.has(param.name)) return true;
        if (param.isFourCornerGroup) {
          const positions = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
          for (const pos of positions) {
            if (updatedParams.has(`${param.name} (${pos})`)) return true;
          }
        }
      }
    }
    return false;
  };

  const [compensations, setCompensations] = useState([]);
  const [showPassphrasePrompt, setShowPassphrasePrompt] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [showAllVehicles, setShowAllVehicles] = useState(false);
  const [expandedEvents, setExpandedEvents] = useState({});
  const [user, loading, error] = useAuthState(auth);
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const location = useLocation();
  // For shared sessions, read/write from the owner's account.
  const ownerUid = location.state?.ownerUid || null;
  const dataUid = ownerUid || (user ? user.uid : null);

  // Map of editor uid -> highlight color, set by the owner per shared user.
  const [editorColors, setEditorColors] = useState({});
  // Map of editor uid -> custom display name the owner assigned per shared user.
  const [editorNames, setEditorNames] = useState({});
  const DEFAULT_ATTR_COLOR = '#f59e0b';

  // Per-field edit attribution. A field is highlighted when it was last edited
  // by someone other than the data owner (the vehicle/event owner).
  const fieldAttr = useCallback((name) => {
    const m = fieldMeta[name];
    if (!m || !m.uid || m.uid === dataUid) return { byOther: false, title: '', color: '' };
    const when = m.time ? new Date(m.time).toLocaleString() : '';
    return {
      byOther: true,
      title: `Edited by ${editorNames[m.uid] || m.email || 'another user'}${when ? ' • ' + when : ''}`,
      color: editorColors[m.uid] || DEFAULT_ATTR_COLOR,
    };
  }, [fieldMeta, dataUid, editorColors, editorNames]);

  // Load the owner's per-user highlight colors from their share docs.
  useEffect(() => {
    if (!dataUid) return;
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(query(collection(db, 'shares'), where('ownerUid', '==', dataUid)));
        if (cancelled) return;
        const map = {};
        const names = {};
        snap.docs.forEach(d => {
          const data = d.data();
          if (data.recipientUid && data.color) map[data.recipientUid] = data.color;
          if (data.recipientUid && data.recipientName) names[data.recipientUid] = data.recipientName;
        });
        setEditorColors(map);
        setEditorNames(names);
      } catch (err) {
        console.error('Error loading share colors:', err.message);
      }
    })();
    return () => { cancelled = true; };
  }, [dataUid]);

  // Function to get unit for a parameter from tabs
  const getUnitForParam = useCallback((paramName) => {
    for (const tab of tabs) {
      for (const group of tab.groups) {
        const param = group.params.find(p => p.name === paramName || paramName.startsWith(p.name + ' ('));
        if (param) return param.unit;
      }
    }
    if (paramName.startsWith('Tire Temp (')) {
      // Find the unit for Tire Temperature
      for (const tab of tabs) {
        for (const group of tab.groups) {
          const param = group.params.find(p => p.name === 'Tire Temperature');
          if (param) return param.unit;
        }
      }
    }
    return '';
  }, [tabs]);

  // Convert params from native units to display units
  const convertParamsToDisplay = useCallback((nativeParams) => {
    const displayParams = {};
    Object.keys(nativeParams).forEach(key => {
      const unit = getUnitForParam(key);
      displayParams[key] = convertFromNative(nativeParams[key], unit);
    });
    return displayParams;
  }, [getUnitForParam]);

  // Convert params from display units to native units
  const convertParamsToNative = useCallback((displayParams) => {
    const nativeParams = {};
    Object.keys(displayParams).forEach(key => {
      const unit = getUnitForParam(key);
      nativeParams[key] = convertToNative(displayParams[key], unit);
    });
    return nativeParams;
  }, [getUnitForParam]);

  useEffect(() => {
    document.documentElement.style.setProperty('--tires-image', `url(${IMAGE_PATHS.Tires})`);
    document.documentElement.style.setProperty('--suspension-image', `url(${IMAGE_PATHS.Suspension})`);
    document.documentElement.style.setProperty('--brakes-image', `url(${IMAGE_PATHS.Brakes})`);
    document.documentElement.style.setProperty('--aero-image', `url(${IMAGE_PATHS.Aero})`);
    document.documentElement.style.setProperty('--drivetrain-image', `url(${IMAGE_PATHS.Drivetrain})`);
    document.documentElement.style.setProperty('--engine-image', `url(${IMAGE_PATHS.Engine})`);
    document.documentElement.style.setProperty('--chassis-image', `url(${IMAGE_PATHS.Chassis})`);
    document.documentElement.style.setProperty('--electronics-image', `url(${IMAGE_PATHS.Electronics})`);
    return () => {
      document.documentElement.style.removeProperty('--tires-image');
      document.documentElement.style.removeProperty('--suspension-image');
      document.documentElement.style.removeProperty('--brakes-image');
      document.documentElement.style.removeProperty('--aero-image');
      document.documentElement.style.removeProperty('--drivetrain-image');
      document.documentElement.style.removeProperty('--engine-image');
      document.documentElement.style.removeProperty('--chassis-image');
      document.documentElement.style.removeProperty('--electronics-image');
    };
  }, []);

  useEffect(() => {
    if (loading) {
      setSyncStatus('syncing');
      return;
    }
    if (error || !user) {
      console.error('Auth error or no user:', error);
      setSyncStatus('Error: Not logged in');
      navigate('/');
      return;
    }

    // Load previousSetup from sessionStorage
    const stored = sessionStorage.getItem('previousSetup');
    if (stored) {
      try {
        previousSetupRef.current = JSON.parse(stored);
      } catch (e) {
        console.error('Error parsing stored previousSetup:', e);
      }
    }

    let loadedVehicleId = location.state?.vehicleId;
    let loadedEventId = location.state?.eventId;

    // Seed group context for a new session created from a multi-vehicle event.
    if (!sessionId) {
      if (location.state?.sessionGroupId) setSessionGroupId(location.state.sessionGroupId);
      if (location.state?.sessionVehicleIds) setSessionVehicleIds(location.state.sessionVehicleIds);
      if (location.state?.sessionName) setSessionName(location.state.sessionName);
      if (location.state?.time) setTime(location.state.time);
    }

    const processSessionData = (sessionData, isFromSnapshot = false) => {
      // Decrypt if encrypted
      if (sessionData.encrypted) {
        const passphrase = getStoredPassphrase();
        if (!passphrase) {
          // Need to prompt for passphrase
          setShowPassphrasePrompt(true);
          return;
        }
        try {
          const decryptedData = decryptData(sessionData.data, passphrase);
          // Handle both old format (entire session encrypted) and new format (only sensitive data encrypted)
          if (decryptedData.vehicleId && decryptedData.eventId) {
            // Old format: entire session was encrypted, so decryptedData contains everything
            sessionData = decryptedData;
          } else {
            // New format: only sensitive data was encrypted, merge with plain metadata
            sessionData = {
              ...sessionData,
              ...decryptedData
            };
          }
          delete sessionData.data; // Remove the encrypted blob
          delete sessionData.encrypted; // Remove the encryption flag
        } catch (error) {
          alert('Invalid passphrase. Please check your encryption settings.');
          navigate('/settings');
          return;
        }
      }

      setSessionName(sessionData.sessionName || '');
      setComment(sessionData.comment || '');
      setTime(sessionData.time || '');
      const loadedParams = sessionData.setup || {};
      // Eagerly-created sessions have no saved setup yet — flag so we seed defaults.
      needsDefaultParamsRef.current = Object.keys(loadedParams).length === 0;
      setLoadedNativeParams({ ...loadedParams, powerData: sessionData.powerData || [] });
      setNotes(sessionData.notes || {});
      setFieldMeta(sessionData.fieldMeta || {});
      previousNotesRef.current = sessionData.notes || {};
      setPowerData((sessionData.powerData || []).sort((a, b) => a.rpm - b.rpm));
      setDamperSetup(sessionData.damperSetup || {});
      loadedVehicleId = sessionData.vehicleId;
      loadedEventId = sessionData.eventId;
      if (sessionData.sessionGroupId) setSessionGroupId(sessionData.sessionGroupId);
      if (sessionData.sessionVehicleIds) setSessionVehicleIds(sessionData.sessionVehicleIds);

      if (isFromSnapshot && previousSetupRef.current) {
        const changed = new Set();
        Object.keys(sessionData.setup || {}).forEach(key => {
          if (JSON.stringify(previousSetupRef.current[key]) !== JSON.stringify(sessionData.setup[key])) {
            changed.add(key);
          }
        });
        setUpdatedParams(prev => new Set([...prev, ...changed]));
        // Rings stay until user interacts with any parameter
      }

      previousSetupRef.current = sessionData.setup;
      sessionStorage.setItem('previousSetup', JSON.stringify(sessionData.setup));
    };

    let unsubscribeSession = null;

    // Prompt to duplicate/import from this vehicle's own previous sessions.
    // Excludes the current doc and any setup-less (not-yet-filled) sessions.
    const checkPreviousSessions = async () => {
      try {
        const sessionsQuery = query(
          collection(db, `users/${dataUid}/sessions`),
          where('vehicleId', '==', Number(loadedVehicleId)),
          orderBy('time', 'desc')
        );
        const sessionsSnap = await getDocs(sessionsQuery);
        let allSessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Decrypt encrypted sessions if needed
        const encryptionEnabled = await isEncryptionEnabled(user, db);
        if (encryptionEnabled) {
          const passphrase = getStoredPassphrase();
          if (passphrase) {
            allSessions = decryptSessions(allSessions, passphrase);
          }
        }

        // Only suggest real, filled-in sessions other than this one.
        allSessions = allSessions.filter(s => s.id !== sessionId && s.setup && Object.keys(s.setup).length > 0);

        let eventSessions = allSessions.filter(s => s.eventId === loadedEventId);
        if (eventSessions.length > 0) {
          setPreviousSession(eventSessions[0]);
          setDuplicateMessage('Duplicate Previous Session?');
        } else if (allSessions.length > 0) {
          setPreviousSession(allSessions[0]);
          setDuplicateMessage('Duplicate Last Session From Previous Event?');
        }
        if (eventSessions.length > 0 || allSessions.length > 0) {
          setShowDuplicatePrompt(true);
        }
      } catch (err) {
        console.error('Previous sessions load error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    const loadSession = async () => {
      setSyncStatus('syncing');
      try {
        if (sessionId) {
          const sessionDoc = await getDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`));
          if (sessionDoc.exists()) {
            processSessionData(sessionDoc.data());
            // First open of an eagerly-created (empty) setup sheet — offer to import.
            if (needsDefaultParamsRef.current) {
              checkPreviousSessions();
            }
          }
          // Set up real-time listener for updates
          unsubscribeSession = onSnapshot(doc(db, `users/${dataUid}/sessions/${sessionId}`), (sessionDoc) => {
            if (sessionDoc.exists()) {
              processSessionData(sessionDoc.data(), true);
            }
          }, (error) => {
            console.error('Session snapshot error:', error);
            setSyncStatus(`Error: ${error.message}`);
          });
        } else {
          checkPreviousSessions();
        }
        if (!loadedVehicleId || !loadedEventId) {
          setSyncStatus('Error: Missing vehicle or event');
          navigate('/');
        } else {
          setVehicleId(Number(loadedVehicleId));
          setEventId(loadedEventId);
        }
      } catch (err) {
        console.error('Session load error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    const loadVehicle = async () => {
      if (!user || !loadedVehicleId) return;
      try {
        const vehicleDoc = await getDoc(doc(db, `users/${dataUid}/vehicles/${loadedVehicleId}`));
        if (vehicleDoc.exists()) {
          const vehicleData = vehicleDoc.data();
          setVehicleName(vehicleData.name || 'Unnamed Vehicle');
          let vehicleTabs = vehicleData.tabs || [];

          // ===========================================
          // MIGRATION CODE - START
          // This code migrates old vehicles with "Chassis" tabs to the new "Suspension" structure
          // Can be safely removed ~6 months after deployment when all vehicles have been migrated
          // ===========================================

          // Migrate old Chassis data to Suspension.Weights for backwards compatibility
          const chassisTab = vehicleTabs.find(tab => tab.name === 'Chassis');
          const suspensionTab = vehicleTabs.find(tab => tab.name === 'Suspension');

          if (chassisTab && suspensionTab) {
            // Ensure Suspension tab has proper group structure
            const defaultSuspensionGroups = convertToGroups(CarTemplates['Club Track Day'].Metric.Suspension);

            // Ensure all default groups exist in suspension tab
            defaultSuspensionGroups.forEach(defaultGroup => {
              const existingGroup = suspensionTab.groups.find(g => g.title === defaultGroup.title);
              if (!existingGroup) {
                suspensionTab.groups.push({ ...defaultGroup });
              } else {
                // Ensure mandatory parameters exist in existing groups
                defaultGroup.params.forEach(defaultParam => {
                  const existingParam = existingGroup.params.find(p => p.name === defaultParam.name);
                  if (!existingParam) {
                    existingGroup.params.push({ ...defaultParam });
                  }
                });
              }
            });

            // Move chassis parameters to suspension Weights group
            const weightsGroup = suspensionTab.groups.find(group => group.title === 'Weights');

            if (weightsGroup && chassisTab.groups && chassisTab.groups.length > 0) {
              // Move all chassis parameters to the Weights group
              chassisTab.groups.forEach(chassisGroup => {
                if (chassisGroup.params) {
                  chassisGroup.params.forEach(param => {
                    // Check if parameter already exists in Weights to avoid duplicates
                    const existingParam = weightsGroup.params.find(p => p.name === param.name);
                    if (!existingParam) {
                      weightsGroup.params.push({ ...param });
                    }
                  });
                }
              });
            }
            // Remove the old Chassis tab
            vehicleTabs = vehicleTabs.filter(tab => tab.name !== 'Chassis');

            // Save the migrated vehicle data back to Firestore
            try {
              await updateDoc(doc(db, `users/${dataUid}/vehicles/${loadedVehicleId}`), {
                tabs: vehicleTabs,
                migratedAt: new Date().toISOString()
              });
            } catch (error) {
              console.error('Failed to save migrated vehicle data:', error);
            }
          }

          // ===========================================
          // MIGRATION CODE - END
          // Remove everything between MIGRATION CODE comments when all vehicles are migrated
          // ===========================================

          const compensationsTab = vehicleTabs.find(t => t.name === 'Compensations');
          setCompensations(compensationsTab?.groups.flatMap(g => g.params) || []);
          setTabs(vehicleTabs.filter(t => t.name !== 'Info' && t.name !== 'Compensations'));
          // Capture the vehicle's default params (used for brand-new sessions and for
          // eagerly-created sessions whose setup sheet is opened for the first time).
          const initialParams = {};
          vehicleTabs.forEach(tab => {
            tab.groups.forEach(group => {
              group.params.forEach(param => {
                if (param.isFourCornerGroup) {
                  ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'].forEach(pos => {
                    initialParams[`${param.name} (${pos})`] = '';
                  });
                } else {
                  initialParams[param.name] = param.value || '';
                }
              });
            });
          });
          defaultParamsRef.current = initialParams;
          if (!sessionId) {
            setParams(initialParams);
            setInitialParams(initialParams);
          }
          setSyncStatus(navigator.onLine ? 'synced' : 'offline');
        } else {
          console.error('Vehicle not found:', loadedVehicleId);
          setSyncStatus('Error: Vehicle not found');
        }
      } catch (err) {
        console.error('Vehicle load error:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };

    const loadTires = async () => {
      if (!user) return;
      try {
        const tiresQuery = query(
          collection(db, `users/${user.uid}/tires`),
          where('active', '==', true),
          orderBy('tireId')
        );
        const tiresSnap = await getDocs(tiresQuery);
        const tiresData = tiresSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setTires(tiresData);

        // Update tabs with tire options for Tire ID
        setTabs(prevTabs => prevTabs.map(tab => {
          if (tab.name === 'Tires') {
            return {
              ...tab,
              groups: tab.groups.map(group => ({
                ...group,
                params: group.params.map(param => {
                  if (param.name === 'Tire ID') {
                    return { ...param, options: ['', ...tiresData.map(t => ({
                      value: t.tireId,
                      label: `${t.tireId}${t.tireSize || t.tireCompound ? ` (${[t.tireSize, t.tireCompound].filter(Boolean).join(', ')})` : ''}`
                    }))] };
                  }
                  return param;
                })
              }))
            };
          }
          return tab;
        }));
      } catch (err) {
        console.error('Tires load error:', err.message);
      }
    };

    loadSession();
    loadVehicle();
    loadTires();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);

    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
      if (unsubscribeSession) unsubscribeSession();
    };
  }, [user, loading, error, sessionId, location.state, navigate]);

  // Convert loaded native params to display params when tabs are loaded
  useEffect(() => {
    if (tabs.length > 0 && loadedNativeParams) {
      let displayParams = convertParamsToDisplay(loadedNativeParams);
      // Seed from vehicle defaults if this session has no saved setup yet.
      if (needsDefaultParamsRef.current) {
        displayParams = { ...defaultParamsRef.current };
        needsDefaultParamsRef.current = false;
      }
      setParams(displayParams);
      setInitialParams(displayParams);
      setInitialData({
        sessionName: sessionName || '',
        comment: comment || '',
        time: time || '',
        params: displayParams,
        notes: notes || {},
        powerData: powerData || [],
        damperSetup: damperSetup || {}
      });
      setLoadedNativeParams(null);
      // Delay to prevent flashing of changed indicators on initial load
      setTimeout(() => setIsInitialLoad(false), 100);
    }
  }, [tabs, loadedNativeParams, sessionName, comment, time, notes, powerData, damperSetup, convertParamsToDisplay]);

  useEffect(() => {
    if (showCompareModal && user) {
      const loadVehicles = async () => {
        try {
          const vehiclesQuery = query(
            collection(db, `users/${dataUid}/vehicles`),
            orderBy('timestamp', 'desc')
          );
          const vehiclesSnap = await getDocs(vehiclesQuery);
          const vehiclesData = vehiclesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setVehicles(vehiclesData);
        } catch (err) {
          console.error('Load vehicles error:', err);
        }
      };
      loadVehicles();
    }
  }, [showCompareModal, user]);

  useEffect(() => {
    if ((showCompareModal || showSelectOther) && user) {
      const currentVehicleId = vehicleId ? vehicleId.toString() : null;
      const loadEventsAndSessions = async () => {
        try {
          const eventsQuery = query(
            collection(db, `users/${dataUid}/events`),
            orderBy('startDate', 'desc')
          );
          const eventsSnap = await getDocs(eventsQuery);
          let eventsData = eventsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          if (!showAllVehicles || showSelectOther) {
            eventsData = eventsData.filter(event => event.vehicleId?.toString() == currentVehicleId);
          }
          setEvents(eventsData);
          const sessionsMap = {};
          // Check if encryption is enabled once for all sessions
          const encryptionEnabled = await isEncryptionEnabled(user, db);
          const passphrase = encryptionEnabled ? getStoredPassphrase() : null;

          for (const event of eventsData) {
            const sessionsQuery = query(
              collection(db, `users/${dataUid}/sessions`),
              where('eventId', '==', event.id),
              orderBy('time', 'desc')
            );
            const sessionsSnap = await getDocs(sessionsQuery);
            let sessions = sessionsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

            // Decrypt encrypted sessions if needed
            if (encryptionEnabled && passphrase) {
              sessions = decryptSessions(sessions, passphrase);
            }

            sessionsMap[event.id] = sessions;
          }
          setSessionsByEvent(sessionsMap);
        } catch (err) {
          console.error('Load events and sessions error:', err);
        }
      };
      loadEventsAndSessions();
    }
  }, [showCompareModal, showSelectOther, showAllVehicles, user, vehicleId]);

  useEffect(() => {
    if (comparison && comparison.eventId && user) {
      const fetchEventName = async () => {
        try {
          const eventDoc = await getDoc(doc(db, `users/${dataUid}/events/${comparison.eventId}`));
          if (eventDoc.exists()) {
            setComparisonEventName(eventDoc.data().name);
          }
        } catch (err) {
          console.error('Fetch event name error:', err);
        }
      };
      fetchEventName();
    } else {
      setComparisonEventName('');
    }
  }, [comparison, user]);

  const handlePowerDataChange = (newPowerData) => {
    const sortedData = newPowerData.sort((a, b) => a.rpm - b.rpm);
    setPowerData(sortedData);
    setParams(prev => ({ ...prev, powerData: sortedData }));
  };

  const handleDamperSetupChange = (newDamperSetup) => {
    setDamperSetup(newDamperSetup);
  };

  const handleDuplicate = async (selectedSession) => {
    // Set time to current date and time in local timezone
    const now = new Date();
    const formattedTime = now.toLocaleString('sv-SE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    }).replace(' ', 'T');
    if (selectedSession) {
      const nativeParams = {};
      tabs.forEach(tab => {
        tab.groups.forEach(group => {
          group.params.forEach(param => {
            if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
              const positions = param.isFrontRearGroup ? ['Front', 'Rear'] : param.leftRight ? ['Left', 'Right'] : ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
              positions.forEach(pos => {
                const paramName = `${param.name} (${pos})`;
                nativeParams[paramName] = selectedSession?.setup?.[paramName] || '';
              });
            } else {
              nativeParams[param.name] = selectedSession?.setup?.[param.name] || param.value || '';
            }
          });
        });
      });
      const newParams = convertParamsToDisplay(nativeParams);
      setParams(newParams);
      setInitialParams(newParams);
      setNotes({});
      setPowerData((selectedSession?.powerData || []).sort((a, b) => a.rpm - b.rpm));
      setParams(prev => ({ ...prev, powerData: (selectedSession?.powerData || []).sort((a, b) => a.rpm - b.rpm) }));
      setDamperSetup(selectedSession?.damperSetup || {});
      setTime(formattedTime);
      setInitialData({
        sessionName: '',
        comment: '',
        time: formattedTime,
        params: { ...newParams, powerData: (selectedSession?.powerData || []).sort((a, b) => a.rpm - b.rpm) },
        notes: {},
        powerData: (selectedSession?.powerData || []).sort((a, b) => a.rpm - b.rpm),
        damperSetup: selectedSession?.damperSetup || {}
      });
    } else {
      const newParams = {};
      tabs.forEach(tab => {
        tab.groups.forEach(group => {
          group.params.forEach(param => {
            if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
              const positions = param.isFrontRearGroup ? ['Front', 'Rear'] : param.leftRight ? ['Left', 'Right'] : ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
              positions.forEach(pos => {
                newParams[`${param.name} (${pos})`] = '';
              });
            } else {
              newParams[param.name] = param.value || '';
            }
          });
        });
      });
      setParams(newParams);
      setInitialParams(newParams);
      setPowerData([]);
      setDamperSetup({});
      setTime(formattedTime);
      setInitialData({
        sessionName: '',
        comment: '',
        time: formattedTime,
        params: newParams,
        notes: {},
        powerData: [],
        damperSetup: {}
      });
    }
  };

  const saveSetup = async (openData = false) => {
    if (!sessionName || !comment) {
      alert('Session name and comment required');
      return;
    }
    if (!eventId || !vehicleId || isNaN(vehicleId)) {
      alert('Event or Vehicle ID not available. Please start a new session from the event page.');
      return;
    }
    const timestamp = Date.now();

    // Collect tire IDs used in this session
    const tireIds = new Set();
    const corners = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
    corners.forEach(corner => {
      const tireId = params[`Tire ID (${corner})`];
      if (tireId && tireId.trim()) {
        tireIds.add(tireId.trim());
      }
    });

    const nativeParams = convertParamsToNative(params);

    // ---- Per-field edit attribution (stored unencrypted) ----
    const editorUid = user?.uid;
    const editorEmail = user?.email || '';
    const editTime = Date.now();
    const baselineSetup = previousSetupRef.current || {};
    const baselineNotes = previousNotesRef.current || {};
    const updatedMeta = { ...fieldMeta };
    Object.keys(nativeParams).forEach(key => {
      if (key === 'powerData') return;
      if (JSON.stringify(nativeParams[key]) !== JSON.stringify(baselineSetup[key])) {
        updatedMeta[key] = { uid: editorUid, email: editorEmail, time: editTime };
      }
    });
    Object.keys(notes || {}).forEach(idx => {
      if ((notes[idx] || '') !== (baselineNotes[idx] || '')) {
        updatedMeta[`__note_${idx}`] = { uid: editorUid, email: editorEmail, time: editTime };
      }
    });
    const attributionFields = {
      fieldMeta: updatedMeta,
      lastEditedBy: editorUid,
      lastEditedByEmail: editorEmail,
      lastEditedAt: editTime,
    };

    // Resolve the doc id up front so a brand-new single-vehicle session can
    // default its group id to its own id (every doc gets a group id going forward).
    const currentId = sessionId || Date.now().toString();
    const groupFields = {
      sessionGroupId: sessionGroupId || currentId,
      sessionVehicleIds: sessionVehicleIds || [Number(vehicleId)],
    };

    // Encrypt session data if encryption is enabled
    const encryptionEnabled = await isEncryptionEnabled(user, db);
    let sessionData;

    if (encryptionEnabled) {
      const passphrase = getStoredPassphrase();
      if (!passphrase) {
        alert('Encryption is enabled but no passphrase found. Please check your settings.');
        return;
      }

      // Store metadata in plain text for queries, encrypt sensitive data
      const sensitiveData = {
        setup: nativeParams,
        powerData: (params.powerData || []).sort((a, b) => a.rpm - b.rpm),
        damperSetup,
        notes
      };

      sessionData = {
        // Plain text metadata for queries and display
        vehicleId: Number(vehicleId),
        eventId,
        sessionName,
        comment,
        time,
        timestamp,
        tireIds: Array.from(tireIds),
        ...groupFields,
        // Encrypted sensitive data
        encrypted: true,
        data: encryptData(sensitiveData, passphrase),
        // Remove old plain text fields to avoid data duplication
        setup: null,
        powerData: null,
        damperSetup: null,
        notes: null,
        ...attributionFields
      };
    } else {
      // No encryption - store everything in plain text
      sessionData = {
        vehicleId: Number(vehicleId),
        eventId,
        setup: nativeParams,
        powerData: (params.powerData || []).sort((a, b) => a.rpm - b.rpm),
        damperSetup,
        notes,
        sessionName,
        comment,
        time,
        timestamp,
        tireIds: Array.from(tireIds),
        ...groupFields,
        encrypted: false,
        // Remove old encrypted field to avoid data duplication
        data: null,
        ...attributionFields
      };
    }

    try {
      setSyncStatus('syncing');

      // Use setDoc with merge to properly handle field deletion when switching encryption modes and create new documents
      const sessionRef = doc(db, `users/${dataUid}/sessions/${currentId}`);

      if (encryptionEnabled) {
        // When encrypting, set encrypted fields and delete plain text fields
        await setDoc(sessionRef, {
          ...sessionData,
          setup: deleteField(),
          powerData: deleteField(),
          damperSetup: deleteField(),
          notes: deleteField()
        }, { merge: true });
      } else {
        // When decrypting, set plain text fields and delete encrypted field
        await setDoc(sessionRef, {
          ...sessionData,
          data: deleteField()
        }, { merge: true });
      }

      // Propagate shared fields (session name & time) to sibling vehicle docs.
      await syncSharedFieldsToGroup(groupFields.sessionGroupId, currentId, { sessionName, time });

      await updateLastModified(user);
      setSyncStatus(navigator.onLine ? 'synced' : 'offline');
      setUpdatedParams(new Set()); // Clear highlights after saving
      if (openData) {
        navigate(`/session-data/${currentId}`, { state: { vehicleId, eventId, ownerUid } });
      } else {
        navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
      }
    } catch (err) {
      console.error('Save setup error:', err.message);
      setSyncStatus(`Error: ${err.message}`);
      alert('Failed to save setup: ' + err.message);
    }
  };

  const selectComparison = () => {
    setShowAllVehicles(false);
    setExpandedEvents({});
    setShowCompareModal(true);
  };

  const hasDifferences = (tab) => {
    if (!comparison) return false;
    // Get all param names for this tab
    const tabParamNames = [];
    tab.groups.forEach(group => {
      group.params.forEach(param => {
        if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
          const positions = param.isFrontRearGroup ? ['Front', 'Rear'] : param.leftRight ? ['Left', 'Right'] : ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
          positions.forEach(pos => {
            tabParamNames.push(`${param.name} (${pos})`);
          });
        } else {
          tabParamNames.push(param.name);
        }
      });
    });
    // Check if any param in this tab differs
    return tabParamNames.some(p => params[p] !== convertFromNative(comparison.setup?.[p], getUnitForParam(p)));
  };

  const handleClose = () => {
    if (hasUnsavedChanges()) {
      setShowDiscardConfirm(true);
    } else {
      navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
    }
  };

  // Write shared session fields to every other doc in the group (multi-vehicle only).
  const syncSharedFieldsToGroup = async (groupId, currentId, fields) => {
    if (!groupId || !sessionVehicleIds || sessionVehicleIds.length <= 1) return;
    try {
      const siblingsSnap = await getDocs(query(
        collection(db, `users/${dataUid}/sessions`),
        where('sessionGroupId', '==', groupId)
      ));
      await Promise.all(
        siblingsSnap.docs
          .filter(d => d.id !== currentId)
          .map(d => updateDoc(d.ref, fields))
      );
    } catch (err) {
      console.error('Group sync error:', err.message);
    }
  };

  const confirmDiscard = () => {
    setShowDiscardConfirm(false);
    navigate(`/event/${eventId}`, { state: { vehicleId, ownerUid } });
  };

  const formatDateRange = (start, end) => {
    if (!start) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : startDate;
    const startMonth = months[startDate.getMonth()];
    const endMonth = months[endDate.getMonth()];
    const startDay = startDate.getDate();
    const endDay = endDate.getDate();
    const year = startDate.getFullYear();
    if (startMonth === endMonth) {
      if (startDay === endDay) {
        return `${startMonth} ${startDay}, ${year}`;
      } else {
        return `${startMonth} ${startDay}-${endDay}, ${year}`;
      }
    } else {
      return `${startMonth} ${startDay}-${endMonth} ${endDay}, ${year}`;
    }
  };

  const formatValue = (value, step, unit) => {
    if (!value) return '';
    if (unit === 'text') return value;
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return '';
    const stepValue = parseFloat(step);
    const decimalPlaces = stepValue < 1 ? (step.toString().split('.')[1]?.length || 1) : 2;
    return numValue.toFixed(decimalPlaces);
  };

  const renderGroupedParams = (param) => {
    let positions;
    if (param.leftRight) {
      positions = ['Left', 'Right'];
    } else if (param.name === 'Left/Right Independent') {
      positions = ['Left', 'Right'];
    } else if (param.isFrontRearGroup) {
      positions = ['Front', 'Rear'];
    } else {
      positions = ['Front Left', 'Front Right', 'Rear Left', 'Rear Right'];
    }
    const paramNames = positions.map(pos => `${param.name} (${pos})`);
    const { unit, step, min, max, type, options } = param;
    return (
      <div className="param-card">
        <h3>{param.name} <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i></h3>
        <div className={`param-grid ${param.isFrontRearGroup ? 'param-grid-vertical' : ''}`}>
          {paramNames.map((pName, idx) => (
            <div key={pName} className={`param-input-group ${param.isFrontRearGroup ? 'param-input-group-horizontal' : ''}`}>
              {param.isFrontRearGroup && <span className="param-position-label">{positions[idx]}</span>}
              {type === 'select' ? (
                <select
                  value={params[pName] || ''}
                  onChange={(e) => handleParamChange(pName, e.target.value)}
                  className={`event-editor-select ${fieldAttr(pName).byOther ? 'edited-by-other' : ''}`}
                  title={fieldAttr(pName).byOther ? fieldAttr(pName).title : undefined}
                  style={fieldAttr(pName).byOther && fieldAttr(pName).color ? { backgroundColor: fieldAttr(pName).color, borderColor: fieldAttr(pName).color } : undefined}
                >
                  {options?.map(option => (
                    <option key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                      {typeof option === 'string' ? option : option.label}
                    </option>
                  ))}
                </select>
              ) : (
                <MobileScrollInput
                  value={formatValue(params[pName], step, unit)}
                  onChange={(val) => handleParamChange(pName, val)}
                  step={step}
                  min={min}
                  max={max}
                  unit={unit}
                  position={positions[idx]}
                  id={pName}
                  isChanged={params[pName] !== (initialParams[pName] ?? '') && !isInitialLoad}
                  isFourCornerGroup={true}
                  isSnapshotUpdate={updatedParams.has(pName)}
                  attributedToOther={fieldAttr(pName).byOther}
                  attributionTitle={fieldAttr(pName).title}
                  attributionColor={fieldAttr(pName).color}
                />
              )}
              {comparison && comparison.setup?.[pName] && (
                <div className={`tab-comparison ${params[pName] === convertFromNative(comparison.setup[pName], unit) ? 'tab-comparison-no-diff' : ''}`}>
                  {type === 'select' ? comparison.setup[pName] : formatValue(convertFromNative(comparison.setup[pName], unit), step, unit)}
                </div>
              )}
            </div>
          ))}
        </div>
        {unit && unit !== 'text' && type !== 'select' && <p className="param-unit-inline">{unit}</p>}
      </div>
    );
  };

  const renderNotesCard = () => (
    <div className="tab-group">
      <h3 className="tab-group-title">Notes</h3>
      <textarea
        value={notes[currentTab] || ''}
        onChange={e => { saveToHistory(); setNotes({ ...notes, [currentTab]: e.target.value }); }}
        placeholder="Notes"
        className={`tab-notes ${fieldAttr(`__note_${currentTab}`).byOther ? 'edited-by-other' : ''}`}
        title={fieldAttr(`__note_${currentTab}`).byOther ? fieldAttr(`__note_${currentTab}`).title : undefined}
        style={fieldAttr(`__note_${currentTab}`).byOther && fieldAttr(`__note_${currentTab}`).color ? { backgroundColor: fieldAttr(`__note_${currentTab}`).color, borderColor: fieldAttr(`__note_${currentTab}`).color } : undefined}
      />
      {comparison && comparison.notes?.[currentTab] && (
        <p className="tab-comparison-notes">{comparison.notes[currentTab]}</p>
      )}
    </div>
  );

  const renderTiresTab = () => {
    return (
      <div className="tire-container">
        {tabs[currentTab].groups.map((group, gIdx) => (
          <div key={gIdx} className="tab-group">
            <h3 className="tab-group-title">{group.title}</h3>
            {group.params.map((param, pIdx) => {
              if (param.isDamperGraph) {
                return (
                  <DamperForceGraph
                    key={param.name}
                    param={param}
                    params={params}
                    vehicleTabs={tabs}
                    sessionId={sessionId}
                    vehicleId={vehicleId}
                    eventId={eventId}
                    damperSetup={damperSetup}
                    onDamperSetupChange={handleDamperSetupChange}
                    onParamChange={handleParamChange}
                    comparison={comparison}
                  />
                );
              }
              if (param.name === 'Tire Temperature') {
                const tireTempLabels = ['FL-O', 'FL-M', 'FL-I', 'FR-I', 'FR-M', 'FR-O', 'RL-O', 'RL-M', 'RL-I', 'RR-I', 'RR-M', 'RR-O'];
                const tireTempArray = tireTempLabels.map(label => params[`Tire Temp (${label})`] || '');
                return (
                  <TireTemperatureInput
                    key={param.name}
                    value={tireTempArray}
                    onChange={(newArray) => {
                      saveToHistory();
                      const newParams = { ...params };
                      tireTempLabels.forEach((label, idx) => {
                        newParams[`Tire Temp (${label})`] = newArray[idx];
                      });
                      setParams(newParams);
                    }}
                    help={param.help}
                    setHelpParam={setHelpParam}
                    setShowHelpModal={setShowHelpModal}
                    param={param}
                  />
                );
              }
              if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                return renderGroupedParams(param);
              }
              const { step, min, max, unit } = param;
              return (
                <div key={param.name} className="tab-param">
                  <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                  <div className="input-comparison-group">
                    <MobileScrollInput
                      value={formatValue(params[param.name], step, unit)}
                      onChange={(val) => handleParamChange(param.name, val)}
                      step={step}
                      min={min}
                      max={max}
                      unit={unit}
                      id={param.name}
                      isChanged={params[param.name] !== (initialParams[param.name] ?? '') && !isInitialLoad}
                      isFourCornerGroup={false}
                      isSnapshotUpdate={updatedParams.has(param.name)}
                      attributedToOther={fieldAttr(param.name).byOther}
                      attributionTitle={fieldAttr(param.name).title}
                      attributionColor={fieldAttr(param.name).color}
                    />
                    {comparison && comparison.setup?.[param.name] && (
                      <div className={`tab-comparison ${params[param.name] === comparison.setup[param.name] ? 'tab-comparison-no-diff' : ''}`}>{formatValue(comparison.setup[param.name], step, unit)}</div>
                    )}
                  </div>
                  <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                </div>
              );
            })}
          </div>
        ))}
        {renderNotesCard()}
      </div>
    );
  };

  const renderSuspensionTab = () => {
    return (
      <div className="param-container">
        {tabs[currentTab].groups.map((group, gIdx) => (
          <div key={gIdx} className="tab-group">
            <h3 className="tab-group-title">{group.title}</h3>
            {group.params.map((param, pIdx) => {
              if (param.isDamperGraph) {
                return (
                  <DamperForceGraph
                    key={param.name}
                    param={param}
                    params={params}
                    vehicleTabs={tabs}
                    sessionId={sessionId}
                    vehicleId={vehicleId}
                    eventId={eventId}
                    damperSetup={damperSetup}
                    onDamperSetupChange={handleDamperSetupChange}
                    onParamChange={handleParamChange}
                    comparison={comparison}
                  />
                );
              }
              if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                return renderGroupedParams(param);
              }
              const { step, min, max, unit } = param;
              return (
                <div key={param.name} className="tab-param">
                  <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                  <div className="input-comparison-group">
                    <MobileScrollInput
                      value={formatValue(params[param.name], step, unit)}
                      onChange={(val) => handleParamChange(param.name, val)}
                      step={step}
                      min={min}
                      max={max}
                      unit={unit}
                      id={param.name}
                      isChanged={params[param.name] !== (initialParams[param.name] ?? '')}
                      isFourCornerGroup={false}
                      isSnapshotUpdate={updatedParams.has(param.name)}
                      attributedToOther={fieldAttr(param.name).byOther}
                      attributionTitle={fieldAttr(param.name).title}
                      attributionColor={fieldAttr(param.name).color}
                    />
                    {comparison && comparison.setup?.[param.name] && (
                      <div className={`tab-comparison ${params[param.name] === convertFromNative(comparison.setup[param.name], unit) ? 'tab-comparison-no-diff' : ''}`}>{formatValue(convertFromNative(comparison.setup[param.name], unit), step, unit)}</div>
                    )}
                  </div>
                  <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                </div>
              );
            })}
          </div>
        ))}
        {renderNotesCard()}
      </div>
    );
  };

  const renderDrivetrainTab = () => {
    return (
      <div className="param-container">
        {tabs[currentTab].groups.map((group, gIdx) => (
          <div key={gIdx} className="tab-group">
            <div className="tab-group-title-container">
              <h3 className="tab-group-title">{group.title}</h3>
            </div>
            {group.params.filter(param => {
              if (param.name.startsWith('Gear Ratio ')) {
                const gearNum = parseInt(param.name.split(' ')[2]);
                const numGears = parseInt(params['Number of Gears'] || '6');
                return gearNum <= numGears;
              }
              return true;
            }).map((param, pIdx) => {
              if (param.isDamperGraph) {
                return (
                  <DamperForceGraph
                    key={param.name}
                    param={param}
                    params={params}
                    vehicleTabs={tabs}
                    sessionId={sessionId}
                    vehicleId={vehicleId}
                    eventId={eventId}
                    damperSetup={damperSetup}
                    onDamperSetupChange={handleDamperSetupChange}
                    onParamChange={handleParamChange}
                    comparison={comparison}
                  />
                );
              }
              if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                return renderGroupedParams(param);
              }
              const { step, min, max, unit, type, options } = param;
              if (type === 'select') {
                return (
                  <div key={param.name} className="tab-param">
                    <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                    <div className="input-comparison-group">
                      <select
                        value={params[param.name] || param.value || (options[0]?.value || options[0])}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                        className={`event-editor-select ${fieldAttr(param.name).byOther ? 'edited-by-other' : ''}`}
                        title={fieldAttr(param.name).byOther ? fieldAttr(param.name).title : undefined}
                        style={fieldAttr(param.name).byOther && fieldAttr(param.name).color ? { backgroundColor: fieldAttr(param.name).color, borderColor: fieldAttr(param.name).color } : undefined}
                      >
                        {options.map(option => (
                          <option key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                            {typeof option === 'string' ? option : option.label}
                          </option>
                        ))}
                      </select>
                      {comparison && comparison.setup?.[param.name] && (
                        <div className={`tab-comparison ${params[param.name] === comparison.setup[param.name] ? 'tab-comparison-no-diff' : ''}`}>{comparison.setup[param.name]}</div>
                      )}
                    </div>
                    <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                  </div>
                );
              }
              return (
                <div key={param.name} className="tab-param">
                  <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                  <div className="input-comparison-group">
                    <MobileScrollInput
                      value={formatValue(params[param.name], step, unit)}
                      onChange={(val) => handleParamChange(param.name, val)}
                      step={step}
                      min={min}
                      max={max}
                      unit={unit}
                      id={param.name}
                      isChanged={params[param.name] !== (initialParams[param.name] ?? '') && !isInitialLoad}
                      isFourCornerGroup={false}
                      isSnapshotUpdate={updatedParams.has(param.name)}
                      attributedToOther={fieldAttr(param.name).byOther}
                      attributionTitle={fieldAttr(param.name).title}
                      attributionColor={fieldAttr(param.name).color}
                    />
                    {comparison && comparison.setup?.[param.name] && (
                      <div className={`tab-comparison ${params[param.name] === convertFromNative(comparison.setup[param.name], unit) ? 'tab-comparison-no-diff' : ''}`}>{formatValue(convertFromNative(comparison.setup[param.name], unit), step, unit)}</div>
                    )}
                  </div>
                  <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                </div>
              );
            })}
          </div>
        ))}
        <GearingCalculator params={params} sessionId={sessionId} vehicleId={vehicleId} eventId={eventId} />
        <TractiveEffortGraph
          params={params}
          sessionId={sessionId}
          vehicleId={vehicleId}
          eventId={eventId}
          powerData={powerData}
        />
        {renderNotesCard()}
      </div>
    );
  };

  const renderEngineTab = () => {
    return (
      <div className="param-container">
        {tabs[currentTab].groups.map((group, gIdx) => (
          <div key={gIdx} className="tab-group">
            <h3 className="tab-group-title">{group.title}</h3>
            {group.params.map((param, pIdx) => {
              if (param.isDamperGraph) {
                return (
                  <DamperForceGraph
                    key={param.name}
                    param={param}
                    params={params}
                    vehicleTabs={tabs}
                    sessionId={sessionId}
                    vehicleId={vehicleId}
                    eventId={eventId}
                    damperSetup={damperSetup}
                    onDamperSetupChange={handleDamperSetupChange}
                    onParamChange={handleParamChange}
                    comparison={comparison}
                  />
                );
              }
              if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                return renderGroupedParams(param);
              }
              const { step, min, max, unit, type, options } = param;
              if (type === 'select') {
                return (
                  <div key={param.name} className="tab-param">
                    <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                    <div className="input-comparison-group">
                      <select
                        value={params[param.name] || param.value || options[0]}
                        onChange={(e) => handleParamChange(param.name, e.target.value)}
                        className={`event-editor-select ${fieldAttr(param.name).byOther ? 'edited-by-other' : ''}`}
                        title={fieldAttr(param.name).byOther ? fieldAttr(param.name).title : undefined}
                        style={fieldAttr(param.name).byOther && fieldAttr(param.name).color ? { backgroundColor: fieldAttr(param.name).color, borderColor: fieldAttr(param.name).color } : undefined}
                      >
                        {options.map(option => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {comparison && comparison.setup?.[param.name] && (
                        <div className={`tab-comparison ${params[param.name] === comparison.setup[param.name] ? 'tab-comparison-no-diff' : ''}`}>{comparison.setup[param.name]}</div>
                      )}
                    </div>
                    <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                  </div>
                );
              }
              return (
                <div key={param.name} className="tab-param">
                  <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                  <div className="input-comparison-group">
                    <MobileScrollInput
                      value={formatValue(params[param.name], step, unit)}
                      onChange={(val) => handleParamChange(param.name, val)}
                      step={step}
                      min={min}
                      max={max}
                      unit={unit}
                      id={param.name}
                      isChanged={params[param.name] !== (initialParams[param.name] ?? '') && !isInitialLoad}
                      isFourCornerGroup={false}
                      isSnapshotUpdate={updatedParams.has(param.name)}
                      attributedToOther={fieldAttr(param.name).byOther}
                      attributionTitle={fieldAttr(param.name).title}
                      attributionColor={fieldAttr(param.name).color}
                    />
                    {comparison && comparison.setup?.[param.name] && (
                      <div className={`tab-comparison ${params[param.name] === convertFromNative(comparison.setup[param.name], unit) ? 'tab-comparison-no-diff' : ''}`}>{formatValue(convertFromNative(comparison.setup[param.name], unit), step, unit)}</div>
                    )}
                  </div>
                  <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                </div>
              );
            })}
          </div>
        ))}
        <EnginePowerGraph
          params={params}
          sessionId={sessionId}
          vehicleId={vehicleId}
          eventId={eventId}
          powerData={powerData}
          onPowerDataChange={handlePowerDataChange}
        />
        {renderNotesCard()}
      </div>
    );
  };

  const hasUnsavedChanges = useCallback(() => {
    if (!initialData) return false;
    return (
      sessionName !== initialData.sessionName ||
      comment !== initialData.comment ||
      time !== initialData.time ||
      JSON.stringify(params) !== JSON.stringify(initialData.params) ||
      JSON.stringify(notes) !== JSON.stringify(initialData.notes) ||
      JSON.stringify(powerData) !== JSON.stringify(initialData.powerData) ||
      JSON.stringify(damperSetup) !== JSON.stringify(initialData.damperSetup)
    );
  }, [sessionName, comment, time, params, notes, powerData, damperSetup, initialData]);



  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault();
        e.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <div className="session-setup-container">
      <Header syncStatus={syncStatus} />
      <div className="page-title">
        <h1>Session Info{vehicleName && <span className="session-info-vehicle">{vehicleName}</span>}</h1>
        <button onClick={handleClose} className="close-button">✕</button>
      </div>
      <div className="session-info">
        <div className="session-info-inputs">
          <div className="tab-param">
            <span className="param-label">Session Name:</span>
            <input
              value={sessionName}
              onChange={e => { saveToHistory(); setSessionName(e.target.value); }}
              placeholder="Session Name"
              className="session-data-input session-data-input-aligned"
            />
          </div>
          <div className="tab-param">
            <span className="param-label">Comment:</span>
            <input
              value={comment}
              onChange={e => { saveToHistory(); setComment(e.target.value); }}
              placeholder="Comment"
              className="session-data-input session-data-input-aligned"
            />
          </div>
          <div className="tab-param">
            <span className="param-label">Date & Time:</span>
            <input
              type="datetime-local"
              value={time}
              onChange={e => { saveToHistory(); setTime(e.target.value); }}
              className="session-data-input session-data-input-aligned"
            />
          </div>
          <div className="tab-param">
            <button onClick={handleUndo} disabled={paramHistory.length === 0} className="btn btn-undo" title="Undo">
              <i className="material-icons">undo</i>
            </button>
            <button onClick={handleRedo} disabled={redoHistory.length === 0} className="btn btn-redo" title="Redo">
              <i className="material-icons">redo</i>
            </button>
          </div>
        </div>
        {comparison && (
          <div className="comparison-info">
            <span>🔴 Comparing To <strong>{comparisonEventName}</strong>, <strong>{comparison.sessionName}</strong>, {(() => {
              const date = new Date(comparison.time);
              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
              return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
            })()}</span>
            <button onClick={() => setComparison(null)} className="close-button-compare">✕</button>
          </div>
        )}
      </div>
      <div className="tab-container">
        <div className="tab-list">
          {tabs.map((tab, idx) => (
            <p
              key={idx}
              onClick={() => setCurrentTab(idx)}
              className={`tab-item ${currentTab === idx ? 'active' : ''} ${hasTabUpdated(tab) ? 'snapshot-update' : ''}`}
              data-tab-name={tab.name}
            >
              {tab.name} {hasDifferences(tab) && <span className="tab-diff-indicator">🔴</span>}
            </p>
          ))}
        </div>
        <div className="tab-content">
          {tabs[currentTab]?.name === 'Tires' ? renderTiresTab() :
           tabs[currentTab]?.name === 'Suspension' ? renderSuspensionTab() :
           tabs[currentTab]?.name === 'Drivetrain' ? renderDrivetrainTab() :
           tabs[currentTab]?.name === 'Engine' ? renderEngineTab() :
           (
            <>
              {tabs[currentTab]?.groups.map((group, gIdx) => (
                <div key={gIdx} className="tab-group">
                  <h3 className="tab-group-title">{group.title}</h3>
                  {group.params.map((param, pIdx) => {
                    if (param.isDamperGraph) {
                      return (
                        <DamperForceGraph
                          key={param.name}
                          param={param}
                          params={params}
                          vehicleTabs={tabs}
                          sessionId={sessionId}
                          vehicleId={vehicleId}
                          eventId={eventId}
                          damperSetup={damperSetup}
                          onDamperSetupChange={handleDamperSetupChange}
                          onParamChange={handleParamChange}
                          comparison={comparison}
                        />
                      );
                    }
                    if (param.isFourCornerGroup || param.leftRight || param.isFrontRearGroup) {
                      return renderGroupedParams(param);
                    }
                    const { step, min, max, unit, type, options } = param;
                    if (type === 'select') {
                      return (
                        <div key={param.name} className="tab-param">
                          <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                          <div className="input-comparison-group">
                            <select
                              value={params[param.name] || param.value || (options[0]?.value || options[0])}
                              onChange={(e) => handleParamChange(param.name, e.target.value)}
                              className={`event-editor-select ${fieldAttr(param.name).byOther ? 'edited-by-other' : ''}`}
                              title={fieldAttr(param.name).byOther ? fieldAttr(param.name).title : undefined}
                        style={fieldAttr(param.name).byOther && fieldAttr(param.name).color ? { backgroundColor: fieldAttr(param.name).color, borderColor: fieldAttr(param.name).color } : undefined}
                            >
                              {options.map(option => (
                                <option key={typeof option === 'string' ? option : option.value} value={typeof option === 'string' ? option : option.value}>
                                  {typeof option === 'string' ? option : option.label}
                                </option>
                              ))}
                            </select>
                            {comparison && comparison.setup?.[param.name] && (
                              <div className={`tab-comparison ${params[param.name] === comparison.setup[param.name] ? 'tab-comparison-no-diff' : ''}`}>{comparison.setup[param.name]}</div>
                            )}
                          </div>
                          <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                        </div>
                      );
                    }
                    return (
                      <div key={param.name} className="tab-param">
                        <span>{param.name}{unit && unit !== 'text' ? <span className="param-unit-inline"> ({unit})</span> : ''}</span>
                        <div className="input-comparison-group">
                          <MobileScrollInput
                            value={formatValue(params[param.name], step, unit)}
                            onChange={(val) => handleParamChange(param.name, val)}
                            step={step}
                            min={min}
                            max={max}
                            unit={unit}
                            id={param.name}
                            isChanged={params[param.name] !== (initialParams[param.name] ?? '') && !isInitialLoad}
                            isFourCornerGroup={false}
                            isSnapshotUpdate={updatedParams.has(param.name)}
                            attributedToOther={fieldAttr(param.name).byOther}
                            attributionTitle={fieldAttr(param.name).title}
                      attributionColor={fieldAttr(param.name).color}
                          />
                          {comparison && comparison.setup?.[param.name] && (
                            <div className={`tab-comparison ${params[param.name] === convertFromNative(comparison.setup[param.name], unit) ? 'tab-comparison-no-diff' : ''}`}>{formatValue(convertFromNative(comparison.setup[param.name], unit), step, unit)}</div>
                          )}
                        </div>
                        <i onClick={() => { setHelpParam(param); setShowHelpModal(true); }} className="material-icons">info</i>
                      </div>
                    );
                  })}
                </div>
              ))}
              {renderNotesCard()}
            </>
          )}
        </div>
      </div>
      <div className="session-buttons">
        <button onClick={selectComparison} className="btn btn-secondary">Compare To</button>
        <button onClick={() => setShowCheatSheet(true)} className="btn btn-secondary">Compensations</button>
        <button onClick={() => saveSetup(false)} className="btn btn-save">Save Setup Sheet</button>
        <button onClick={() => saveSetup(true)} className="btn btn-save">Start Session</button>
      </div>
      {showDuplicatePrompt && (
        <div className="duplicate-dialog-overlay">
          <div className="duplicate-dialog">
            <p>{duplicateMessage}</p>
            <div className="duplicate-dialog-actions">
              <button onClick={() => { handleDuplicate(previousSession); setShowDuplicatePrompt(false); }} className="btn btn-save">Yes</button>
              <button onClick={() => { setExpandedEvents({}); setShowSelectOther(true); setShowDuplicatePrompt(false); }} className="btn btn-secondary">Select Other</button>
              <button onClick={() => { handleDuplicate(null); setShowDuplicatePrompt(false); }} className="btn btn-secondary">No</button>
            </div>
          </div>
        </div>
      )}
      {showDiscardConfirm && (
        <ConfirmationDialog
          message="Are you sure you want to discard unsaved changes?"
          onConfirm={confirmDiscard}
          onCancel={() => setShowDiscardConfirm(false)}
        />
      )}
      {showSelectOther && (
        <div className="select-other-overlay">
          <div className="select-other-dialog" style={{ position: 'relative' }}>
            <button onClick={() => setShowSelectOther(false)} className="close-button" style={{ position: 'absolute', top: '10px', right: '10px' }}>✕</button>
            <h2>Select Session to Duplicate</h2>
            <div className="events-list">
              {(() => {
                const toggleEvent = (eventId) => {
                  setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
                };
                return events.map(event => (
                  <div key={event.id} className="event-group">
                    <h3 onClick={() => toggleEvent(event.id)} style={{ cursor: 'pointer' }}>
                      {expandedEvents[event.id] ? '▼' : '▶'} {event.name}
                    </h3>
                    <p>{event.circuit} - {formatDateRange(event.startDate, event.endDate)}</p>
                    {expandedEvents[event.id] && (
                      <div className="event-sessions">
                        {sessionsByEvent[event.id]?.map(session => (
                          <div key={session.id} onClick={() => { handleDuplicate(session); setShowSelectOther(false); }} className="session-card">
                            <div className="session-card-header">
                              <h1>{session.sessionName || 'Unnamed Session'} ({session.bestLap || 'N/A'})</h1>
                            </div>
                            <div className="session-card-meta">
                              <span>Laps: {session.numLaps || '0'}</span>
                            </div>
                            <div className="session-card-notes">
                              <span>Notes: {session.comment || 'No notes'}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
          </div>
        </div>
      )}
      {showCompareModal && (
        <div className="select-other-overlay">
          <div className="select-other-dialog" style={{ position: 'relative' }}>
            <button onClick={() => setShowCompareModal(false)} className="close-button" style={{ position: 'absolute', top: '10px', right: '10px' }}>✕</button>
            <h2>Select Session To Compare To</h2>
            {!showAllVehicles && (
              <button onClick={() => setShowAllVehicles(true)} className="btn btn-secondary">Compare to Different Vehicle</button>
            )}
            <div className="events-list">
              {(() => {
                const toggleEvent = (eventId) => {
                  setExpandedEvents(prev => ({ ...prev, [eventId]: !prev[eventId] }));
                };
                if (showAllVehicles) {
                  // Group events by vehicle
                  const vehicleMap = vehicles.reduce((map, vehicle) => {
                    map[vehicle.id] = vehicle.name;
                    return map;
                  }, {});
                  const eventsByVehicle = events.reduce((groups, event) => {
                    const vehicleId = event.vehicleId;
                    if (!groups[vehicleId]) groups[vehicleId] = [];
                    groups[vehicleId].push(event);
                    return groups;
                  }, {});
                  return Object.entries(eventsByVehicle).map(([vehicleId, vehicleEvents]) => (
                    <div key={vehicleId} className="vehicle-group">
                      <h2>{vehicleMap[vehicleId] || 'Unknown Vehicle'}</h2>
                      {vehicleEvents.map(event => (
                        <div key={event.id} className="event-group">
                          <h3 onClick={() => toggleEvent(event.id)} style={{ cursor: 'pointer' }}>
                            {expandedEvents[event.id] ? '▼' : '▶'} {event.name}
                          </h3>
                          <p>{event.circuit} - {formatDateRange(event.startDate, event.endDate)}</p>
                          {expandedEvents[event.id] && (
                            <div className="event-sessions">
                              {sessionsByEvent[event.id]?.map(session => (
                                <div key={session.id} onClick={() => { setComparison({ ...session, powerData: (session.powerData || []).sort((a, b) => a.rpm - b.rpm) }); setShowCompareModal(false); }} className="session-card">
                                  <div className="session-card-header">
                                    <h1>{session.sessionName || 'Unnamed Session'} ({session.bestLap || 'N/A'})</h1>
                                  </div>
                                  <div className="session-card-meta">
                                    <span>Laps: {session.numLaps || '0'}</span>
                                  </div>
                                  <div className="session-card-notes">
                                    <span>Notes: {session.comment || 'No notes'}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ));
                } else {
                  // Show events for current vehicle
                  return events.map(event => (
                    <div key={event.id} className="event-group">
                      <h3 onClick={() => toggleEvent(event.id)} style={{ cursor: 'pointer' }}>
                        {expandedEvents[event.id] ? '▼' : '▶'} {event.name}
                      </h3>
                      <p>{event.circuit} - {formatDateRange(event.startDate, event.endDate)}</p>
                      {expandedEvents[event.id] && (
                        <div className="event-sessions">
                          {sessionsByEvent[event.id]?.map(session => (
                            <div key={session.id} onClick={() => { setComparison({ ...session, powerData: (session.powerData || []).sort((a, b) => a.rpm - b.rpm) }); setShowCompareModal(false); }} className="session-card">
                              <div className="session-card-header">
                                <h1>{session.sessionName || 'Unnamed Session'} ({session.bestLap || 'N/A'})</h1>
                              </div>
                              <div className="session-card-meta">
                                <span>Laps: {session.numLaps || '0'}</span>
                              </div>
                              <div className="session-card-notes">
                                <span>Notes: {session.comment || 'No notes'}</span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ));
                }
              })()}
            </div>
          </div>
        </div>
      )}
      {showHelpModal && helpParam && (
        <div className="help-modal-overlay" onClick={() => setShowHelpModal(false)}>
          <div className="help-modal" onClick={e => e.stopPropagation()}>
            <div className="help-modal-header">
              <h3>{helpParam.name}</h3>
              <button className="close-button" onClick={() => setShowHelpModal(false)}>✕</button>
            </div>
            <div className="help-modal-content">
              {helpParam.help || 'No help available'}
            </div>
          </div>
        </div>
      )}
      <CompensationDisplay
        showCheatSheet={showCheatSheet}
        setShowCheatSheet={setShowCheatSheet}
        tabs={tabs}
        compensations={compensations}
        getUnitForParam={getUnitForParam}
      />

      {showPassphrasePrompt && (
        <PassphrasePrompt
          message="Enter your encryption passphrase to access this session:"
          onSuccess={(passphrase) => {
            setShowPassphrasePrompt(false);
            // Store the passphrase and retry the original loadSession function
            storePassphrase(passphrase);
            // Re-run the useEffect that loads the session
            const loadSession = async () => {
              setSyncStatus('syncing');
              try {
                if (sessionId) {
                  const sessionDoc = await getDoc(doc(db, `users/${dataUid}/sessions/${sessionId}`));
                  if (sessionDoc.exists()) {
                    let sessionData = sessionDoc.data();

                    // Decrypt if encrypted
                    if (sessionData.encrypted) {
                      try {
                        const decryptedData = decryptData(sessionData.data, passphrase);
                        // Handle both old and new formats
                        if (decryptedData.vehicleId && decryptedData.eventId) {
                          // Old format: entire session was encrypted
                          sessionData = decryptedData;
                        } else {
                          // New format: merge with plain metadata
                          sessionData = {
                            ...sessionData,
                            ...decryptedData
                          };
                        }
                        delete sessionData.data; // Remove the encrypted blob
                        delete sessionData.encrypted; // Remove the encryption flag
                      } catch (error) {
                        alert('Invalid passphrase. Please check your encryption settings.');
                        navigate('/settings');
                        return;
                      }
                    }

                    setSessionName(sessionData.sessionName || '');
                    setComment(sessionData.comment || '');
                    setTime(sessionData.time || '');
                    const loadedParams = sessionData.setup || {};
                    setLoadedNativeParams({ ...loadedParams, powerData: sessionData.powerData || [] });
                    setNotes(sessionData.notes || {});
                    setPowerData((sessionData.powerData || []).sort((a, b) => a.rpm - b.rpm));
                    setDamperSetup(sessionData.damperSetup || {});
                    setVehicleId(sessionData.vehicleId);
                    setEventId(sessionData.eventId);
                  }
                } else {
                  // Handle the case when there's no sessionId (new session)
                  // This part would need to be adapted based on the original logic
                }
                setSyncStatus(navigator.onLine ? 'synced' : 'offline');
              } catch (err) {
                console.error('Load session error:', err);
                setSyncStatus(`Error: ${err.message}`);
              }
            };
            loadSession();
          }}
          onCancel={() => {
            setShowPassphrasePrompt(false);
            navigate('/settings');
          }}
        />
      )}
    </div>
  );
}

export default SessionSetup;